"use client";

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * The current user's profile picture (avatar), backed by
 * `app_users.user_prefs.avatarUrl` via `/api/user/avatar`.
 *
 * Shared cache key so the header trigger, the dropdown header, and the /account
 * Profile tab all read the SAME value and update together — uploading or
 * removing from any surface invalidates the one query.
 */
const AVATAR_QUERY_KEY = ["user", "avatar"] as const;

async function fetchAvatar(): Promise<string | null> {
  const res = await fetch("/api/user/avatar", { credentials: "same-origin" });
  if (!res.ok) return null; // unauthenticated / error → no custom photo
  const body = (await res.json()) as { avatarUrl?: string | null };
  return body.avatarUrl ?? null;
}

async function uploadAvatar(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch("/api/user/avatar", {
    method: "POST",
    credentials: "same-origin",
    body: formData,
  });
  const body = (await res.json().catch(() => ({}))) as {
    avatarUrl?: string | null;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(typeof body.error === "string" ? body.error : "Failed to upload image");
  }
  return body.avatarUrl ?? null;
}

async function deleteAvatar(): Promise<null> {
  const res = await fetch("/api/user/avatar", {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(typeof body.error === "string" ? body.error : "Failed to remove image");
  }
  return null;
}

export interface UseProfileAvatarResult {
  avatarUrl: string | null;
  isLoading: boolean;
  isMutating: boolean;
  /** Upload a new picture. Returns the new public URL. */
  upload: (file: File) => Promise<string | null>;
  /** Remove the current picture (falls back to initials). */
  remove: () => Promise<void>;
}

export function useProfileAvatar(): UseProfileAvatarResult {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: AVATAR_QUERY_KEY,
    queryFn: fetchAvatar,
    staleTime: 5 * 60 * 1000,
  });

  const uploadMutation = useMutation({
    mutationFn: uploadAvatar,
    onSuccess: (url) => {
      queryClient.setQueryData(AVATAR_QUERY_KEY, url);
    },
  });

  const removeMutation = useMutation({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      queryClient.setQueryData(AVATAR_QUERY_KEY, null);
    },
  });

  const upload = useCallback((file: File) => uploadMutation.mutateAsync(file), [uploadMutation]);
  const remove = useCallback(async () => {
    await removeMutation.mutateAsync();
  }, [removeMutation]);

  return {
    avatarUrl: data ?? null,
    isLoading,
    isMutating: uploadMutation.isPending || removeMutation.isPending,
    upload,
    remove,
  };
}
